/**
 * FlagIcon — flaga języka rysowana JAKO SVG, a nie emoji.
 *
 * <para><b>Dlaczego to musiało powstać.</b> Flagi przy wyborze języka były parami znaków
 * „regional indicator" (🇵🇱, 🇬🇧…). To, czy przeglądarka narysuje z nich flagę, zależy WYŁĄCZNIE
 * od fontu systemu: Windows nie ma w Segoe UI Emoji glifów flag państw, więc Chrome i Edge
 * pokazują tam gołe litery „PL", „GB". Firefox wozi własny font z flagami, stąd różnica między
 * przeglądarkami, którą zgłosił właściciel („na Chrome nie wczytują się flagi"). Żadna zmiana
 * w kodzie tego nie naprawi, dopóki obrazek nie pochodzi z aplikacji — dlatego rysujemy sami.</para>
 *
 * Flagi budujemy z prostych figur (pasy poziome/pionowe, krzyż skandynawski, klin przy drzewcu),
 * co wystarcza dla języków z katalogu; Union Jack jest opisany osobno. Dla kodu spoza mapy
 * pokazujemy CZYTELNY skrót (np. „TR”) zamiast pustego miejsca albo liter po emoji — lepiej
 * uczciwy skrót niż udawana flaga.
 */
import React from "react";
import s from "./FlagIcon.module.css";

// Pasy poziome (od góry) / pionowe (od lewej) — najczęstszy układ flag europejskich.
const poziome = (...kolory) => ({ typ: "poziome", kolory });
const pionowe = (...kolory) => ({ typ: "pionowe", kolory });
// Krzyż skandynawski: tło + kolor krzyża (+ opcjonalna obwódka krzyża).
const krzyz = (tlo, kolorKrzyza, obwodka) => ({ typ: "krzyz", tlo, kolorKrzyza, obwodka });
// Klin przy drzewcu (Czechy): dwa pasy + trójkąt od lewej.
const klin = (gora, dol, kolorKlina) => ({ typ: "klin", gora, dol, kolorKlina });

/**
 * Kod języka (Google Translate) → flaga. Kluczem jest JĘZYK, nie państwo: „en" pokazuje flagę
 * Wielkiej Brytanii, bo tak wygląda ten wybór w interfejsie od początku.
 */
const FLAGI = {
    pl: poziome("#ffffff", "#dc143c"),
    en: { typ: "uk" },
    // "uk" to kod JEZYKA ukrainskiego w Google Translate (nie Wielkiej Brytanii) — flaga Ukrainy.
    uk: poziome("#0057b7", "#ffd700"),
    de: poziome("#000000", "#dd0000", "#ffce00"),
    cs: klin("#ffffff", "#d7141a", "#11457e"),
    sk: poziome("#ffffff", "#0b4ea2", "#ee1c25"),
    ru: poziome("#ffffff", "#0039a6", "#d52b1e"),
    be: poziome("#c8313e", "#4aa657"),
    lt: poziome("#fdb913", "#006a44", "#c1272d"),
    lv: poziome("#9e3039", "#ffffff", "#9e3039"),
    et: poziome("#0072ce", "#000000", "#ffffff"),
    hu: poziome("#ce2939", "#ffffff", "#477050"),
    ro: pionowe("#002b7f", "#fcd116", "#ce1126"),
    bg: poziome("#ffffff", "#00966e", "#d62612"),
    fr: pionowe("#002395", "#ffffff", "#ed2939"),
    it: pionowe("#009246", "#ffffff", "#ce2b37"),
    es: poziome("#aa151b", "#f1bf00", "#aa151b"),
    pt: pionowe("#006600", "#006600", "#ff0000"),
    nl: poziome("#ae1c28", "#ffffff", "#21468b"),
    sv: krzyz("#006aa7", "#fecc00"),
    no: krzyz("#ba0c2f", "#00205b", "#ffffff"),
    da: krzyz("#c8102e", "#ffffff"),
    fi: krzyz("#ffffff", "#002f6c"),
    tr: { typ: "tr" },
    el: poziome("#0d5eaf", "#ffffff", "#0d5eaf", "#ffffff", "#0d5eaf"),
    hr: poziome("#ff0000", "#ffffff", "#171796"),
    sl: poziome("#ffffff", "#0000ff", "#ff0000"),
    sr: poziome("#c6363c", "#0c4076", "#ffffff"),
};
const W = 21;   // szerokość rysunku — proporcja 3:2, taka jak większość flag europejskich
const H = 14;

const Pasy = ({ def }) => {
    const n = def.kolory.length;
    if (def.typ === "poziome") {
        const h = H / n;
        return def.kolory.map((c, i) => <rect key={i} x="0" y={i * h} width={W} height={h} fill={c} />);
    }
    const w = W / n;
    return def.kolory.map((c, i) => <rect key={i} x={i * w} y="0" width={w} height={H} fill={c} />);
};

const Krzyz = ({ def }) => (
    <>
        <rect x="0" y="0" width={W} height={H} fill={def.tlo} />
        {def.obwodka && (
            <>
                <rect x="0" y={H / 2 - 2.6} width={W} height="5.2" fill={def.obwodka} />
                <rect x="5.4" y="0" width="5.2" height={H} fill={def.obwodka} />
            </>
        )}
        <rect x="0" y={H / 2 - 1.5} width={W} height="3" fill={def.kolorKrzyza} />
        <rect x="6.3" y="0" width="3" height={H} fill={def.kolorKrzyza} />
    </>
);

const Klin = ({ def }) => (
    <>
        <rect x="0" y="0" width={W} height={H / 2} fill={def.gora} />
        <rect x="0" y={H / 2} width={W} height={H / 2} fill={def.dol} />
        <polygon points={`0,0 ${W / 2},${H / 2} 0,${H}`} fill={def.kolorKlina} />
    </>
);

// Union Jack w uproszczeniu: przekątne białe i czerwone + krzyż św. Jerzego.
const UnionJack = () => (
    <>
        <rect x="0" y="0" width={W} height={H} fill="#012169" />
        <g stroke="#ffffff" strokeWidth="3">
            <line x1="0" y1="0" x2={W} y2={H} />
            <line x1={W} y1="0" x2="0" y2={H} />
        </g>
        <g stroke="#c8102e" strokeWidth="1.4">
            <line x1="0" y1="0" x2={W} y2={H} />
            <line x1={W} y1="0" x2="0" y2={H} />
        </g>
        <rect x="0" y={H / 2 - 2.2} width={W} height="4.4" fill="#ffffff" />
        <rect x={W / 2 - 2.2} y="0" width="4.4" height={H} fill="#ffffff" />
        <rect x="0" y={H / 2 - 1.2} width={W} height="2.4" fill="#c8102e" />
        <rect x={W / 2 - 1.2} y="0" width="2.4" height={H} fill="#c8102e" />
    </>
);

const Turcja = () => (
    <>
        <rect x="0" y="0" width={W} height={H} fill="#e30a17" />
        <circle cx="8.2" cy="7" r="3.4" fill="#ffffff" />
        <circle cx="9.4" cy="7" r="2.7" fill="#e30a17" />
        <polygon points="12.4,7 14.9,5.9 14.1,7 14.9,8.1" fill="#ffffff" />
    </>
);

/**
 * @param {string} code   kod języka (gtCode), np. "pl", "en", "uk"
 * @param {string} label  nazwa języka — trafia do atrybutu title/aria-label
 * @param {string} fallback  skrót pokazywany, gdy nie mamy rysunku (np. "TR"); domyślnie kod
 */
const FlagIcon = ({ code, label, fallback, size = 16, className }) => {
    const kod = String(code || "").toLowerCase();
    const def = FLAGI[kod];
    const opis = label || kod.toUpperCase();

    if (!def) {
        // Uczciwie: nie mamy rysunku tej flagi. Skrót językowy zawsze się wyświetli — w każdej
        // przeglądarce i przy każdym foncie systemowym.
        return (
            <span className={`${s.skrot} ${className || ""}`} title={opis} aria-label={opis} role="img">
                {(fallback || kod).toUpperCase().slice(0, 3)}
            </span>
        );
    }

    return (
        <svg
            className={`${s.flaga} ${className || ""}`}
            width={size * 1.5}
            height={size}
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={opis}
        >
            {/* Nazwa jezyka idzie WYLACZNIE przez aria-label. Element <title> w SVG liczy sie jako
                TEKST na stronie - obok widocznej etykiety w liscie dawal drugie, niewidoczne
                wystapienie tej samej nazwy (mylace dla czytnikow ekranu i dla testow). */}
            {def.typ === "uk" && <UnionJack />}
            {def.typ === "tr" && <Turcja />}
            {(def.typ === "poziome" || def.typ === "pionowe") && <Pasy def={def} />}
            {def.typ === "krzyz" && <Krzyz def={def} />}
            {def.typ === "klin" && <Klin def={def} />}
            {/* Cienka ramka — bez niej biała część flagi (np. polskiej) znika na jasnym tle. */}
            <rect x="0.25" y="0.25" width={W - 0.5} height={H - 0.5} fill="none" stroke="rgba(15,23,42,0.28)" strokeWidth="0.5" />
        </svg>
    );
};

/** Czy dla tego kodu mamy narysowaną flagę (przydatne w panelach konfiguracyjnych). */
export const maFlage = (code) => !!FLAGI[String(code || "").toLowerCase()];

export default FlagIcon;
